<template>
  <b-field :label="label" expanded>
    <b-dropdown
      ref="dropdown"
      v-model="isOpen"
      class="filtro-multi-select"
      aria-role="list"
      expanded
      scrollable
      max-height="260px"
      :mobile-modal="false"
      :close-on-click="false"
      :position="dropdownPosition"
      @active-change="onActiveChange"
    >
      <template #trigger>
        <button
          type="button"
          class="button is-fullwidth filtro-multi-select__control"
          :disabled="loading"
        >
          <span class="filtro-multi-select__content">
            <template v-if="selectedOptions.length">
              <span v-for="option in visibleTags" :key="option.id" class="filtro-multi-select__tag">
                <span class="filtro-multi-select__tag-label">{{
                  option.nome || option.label
                }}</span>
                <button
                  type="button"
                  class="filtro-multi-select__tag-close"
                  aria-label="Remover"
                  @click.stop.prevent="removeTag(option.id)"
                >
                  <b-icon icon="close" size="is-small" custom-size="mdi-14px" />
                </button>
              </span>
              <span
                v-if="hiddenCount > 0"
                class="filtro-multi-select__tag filtro-multi-select__tag--more"
              >
                <span class="filtro-multi-select__tag-label">+{{ hiddenCount }}</span>
              </span>
            </template>
            <span v-else class="filtro-multi-select__tag filtro-multi-select__tag--todos">
              <span class="filtro-multi-select__tag-label">Todos</span>
            </span>
          </span>
          <b-icon
            v-if="selectedOptions.length"
            icon="close-circle"
            class="filtro-multi-select__clear is-clickable"
            aria-label="Limpar"
            @click.native.stop.prevent="clearAll"
          />
          <b-icon
            v-else-if="loading"
            icon="loading"
            size="is-small"
            class="filtro-multi-select__caret"
          />
          <b-icon v-else icon="menu-down" size="is-small" class="filtro-multi-select__caret" />
        </button>
      </template>

      <b-dropdown-item
        v-for="option in options"
        :key="option.id"
        custom
        aria-role="listitem"
        @click.native.prevent="selectOption(option.id)"
      >
        <b-checkbox :value="hasValue(option.id)" :native-value="option.id">
          {{ option.nome || option.label }}
        </b-checkbox>
      </b-dropdown-item>
    </b-dropdown>
  </b-field>
</template>

<script>
export default {
  name: 'FiltroMultiSelect',
  props: {
    label: { type: String, required: true },
    options: { type: Array, default: () => [] },
    value: { type: Array, default: () => [] },
    maxVisibleTags: { type: Number, default: 1 },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      isOpen: false,
      dropdownPosition: 'is-bottom-right',
      dirtyWhileOpen: false,
    };
  },
  computed: {
    selectedOptions() {
      return this.options.filter(option => this.hasValue(option.id));
    },
    visibleTags() {
      return this.selectedOptions.slice(0, this.maxVisibleTags);
    },
    hiddenCount() {
      return Math.max(0, this.selectedOptions.length - this.maxVisibleTags);
    },
  },
  methods: {
    hasValue(id) {
      return this.value.map(String).includes(String(id));
    },
    toggle(id) {
      return this.hasValue(id)
        ? this.value.filter(value => String(value) !== String(id))
        : [...this.value, id];
    },
    selectOption(id) {
      this.dirtyWhileOpen = true;
      this.$emit('input', this.toggle(id));
      // Mantém aberto para permitir múltiplas seleções; aplica ao fechar (clique fora).
    },
    removeTag(id) {
      this.$emit('input', this.toggle(id));
      if (this.isOpen) {
        this.dirtyWhileOpen = true;
        return;
      }
      this.$nextTick(() => this.$emit('commit'));
    },
    clearAll() {
      this.$emit('input', []);
      if (this.isOpen) {
        this.dirtyWhileOpen = true;
        return;
      }
      this.$nextTick(() => this.$emit('commit'));
    },
    onActiveChange(open) {
      if (open) {
        this.dirtyWhileOpen = false;
        this.$emit('open');
        this.updateDropdownPosition();
        return;
      }
      if (this.dirtyWhileOpen) {
        this.dirtyWhileOpen = false;
        this.$emit('commit');
        return;
      }
      this.$emit('close');
    },
    updateDropdownPosition() {
      const root = (this.$refs.dropdown && this.$refs.dropdown.$el) || this.$el;
      if (!root || typeof root.getBoundingClientRect !== 'function') {
        this.dropdownPosition = 'is-bottom-right';
        return;
      }

      const trigger = root.querySelector('.dropdown-trigger') || root;
      const rect = trigger.getBoundingClientRect();
      const midpoint = window.innerWidth / 2;
      const triggerCenter = rect.left + rect.width / 2;

      // Perto da margem esquerda: ancora à esquerda (cresce para a direita).
      // Caso contrário: ancora à direita (cresce para a esquerda).
      this.dropdownPosition = triggerCenter < midpoint ? 'is-bottom-right' : 'is-bottom-left';
    },
  },
};
</script>

<style lang="scss" scoped>
.filtro-multi-select {
  width: 100%;

  // Largura mínima = campo; se o texto não couber, o menu cresce (max-content).
  &.is-expanded ::v-deep .dropdown-menu {
    max-width: calc(100vw - 1rem);
    min-width: 100%;
    width: max-content;
  }

  ::v-deep .dropdown-item {
    white-space: nowrap;
  }

  ::v-deep .dropdown-item .control,
  ::v-deep .dropdown-item .checkbox {
    white-space: nowrap;
  }

  ::v-deep .dropdown-item .checkbox {
    align-items: center;
    display: inline-flex;
  }
}

.filtro-multi-select__control {
  align-items: center;
  display: flex;
  height: 2.5em;
  justify-content: space-between;
  min-height: 2.5em;
  padding-left: 0.5rem;
  padding-right: 0.75rem;
}

.filtro-multi-select__content {
  align-items: center;
  display: flex;
  flex: 1;
  gap: 0.35rem;
  min-width: 0;
  overflow: hidden;
  padding-right: 0.25rem;
}

.filtro-multi-select__clear {
  color: #dbdbdb;
  cursor: pointer;
  flex-shrink: 0;
  pointer-events: auto;
}

.filtro-multi-select__control:focus .filtro-multi-select__clear,
.filtro-multi-select__control.is-focused .filtro-multi-select__clear,
.filtro-multi-select__control:active .filtro-multi-select__clear,
.filtro-multi-select.is-active .filtro-multi-select__clear {
  color: #dbdbdb;
}

.filtro-multi-select__clear:hover,
.filtro-multi-select__control:focus .filtro-multi-select__clear:hover,
.filtro-multi-select__control:active .filtro-multi-select__clear:hover,
.filtro-multi-select.is-active .filtro-multi-select__clear:hover {
  color: #7a7a7a;
}

.filtro-multi-select__caret {
  color: #7a7a7a;
  flex-shrink: 0;
}

.filtro-multi-select__tag {
  align-items: center;
  background: #f5f5f5;
  border-radius: 2px;
  color: #363636;
  cursor: default;
  display: inline-flex;
  flex-shrink: 1;
  font-size: 0.75rem;
  gap: 0.5rem;
  max-width: 100%;
  min-width: 0;
  padding: 0.15rem 0.5rem;
}

.filtro-multi-select__tag--todos,
.filtro-multi-select__tag--more {
  flex-shrink: 0;
}

.filtro-multi-select__tag-label {
  cursor: default;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filtro-multi-select__tag-close {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 50%;
  color: #808080;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  line-height: 1;
  padding: 0.05rem;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover,
  &:focus {
    background-color: #fff;
    color: #f14668;
    outline: none;
  }
}
</style>
